import type { UseQueryResult } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'

/**
 * The three states every list or panel has besides its data (WP-05), in one place:
 * - loading (no data yet): `skeleton`, shaped like the content (design §10: no spinners);
 * - error with nothing to show: ErrorState with the cause and «Повторить» (`refetch`) — a
 *   failed request never renders as «нет данных»;
 * - loaded but empty (`isEmpty`): `empty`, an EmptyState with one action.
 * A background refetch that fails keeps the data on screen; the shell shows a toast for it
 * (QueryErrorToasts).
 */
export function QueryState<T>({
  query,
  skeleton,
  errorTitle,
  isEmpty,
  empty,
  framed = false,
  children,
}: {
  query: Pick<UseQueryResult<T>, 'data' | 'error' | 'isFetching' | 'refetch'>
  skeleton: React.ReactNode
  /** «Не удалось загрузить выплаты» */
  errorTitle: string
  isEmpty?: (data: T) => boolean
  empty?: React.ReactNode
  /** the error sits in a card of its own (when the content it replaces is not in a card) */
  framed?: boolean
  children: (data: T) => React.ReactNode
}) {
  if (query.data === undefined) {
    if (query.error) {
      const error = (
        <ErrorState
          title={errorTitle}
          error={query.error}
          retrying={query.isFetching}
          onRetry={() => void query.refetch()}
        />
      )
      return framed ? <Card>{error}</Card> : error
    }
    return <>{skeleton}</>
  }
  if (empty !== undefined && isEmpty?.(query.data)) return <>{empty}</>
  return <>{children(query.data)}</>
}
